import { Injectable } from "../../../lib/web/reflection/injectable";
import { Key } from "../../../lib/web/core/key";

@Injectable({ type: 'singleton' })
export class CardNavigationService {

    private _keydownHandler: (event: KeyboardEvent) => void = null;
    
    public constructor() {
        this._keydownHandler = this.handleKeyDown.bind(this);
    }

    public enableGridNavigation(gridElement: HTMLElement): void {
        this.disableNavigation(gridElement);
        gridElement.addEventListener('keydown', this._keydownHandler);
        gridElement.dataset.cardNavigation = 'grid';
    }

    public enableRowNavigation(rowElement: HTMLElement): void {
        this.disableNavigation(rowElement);
        rowElement.addEventListener('keydown', this._keydownHandler);
        rowElement.dataset.cardNavigation = 'row';
    }

    public disableNavigation(element: HTMLElement): void {
        element.removeEventListener('keydown', this._keydownHandler);
        delete element.dataset.cardNavigation;
    }

    private handleKeyDown(event: KeyboardEvent): void {
        const target: HTMLElement = event.target as HTMLElement;
        const container: HTMLElement = this.findNavigationContainer(target);        
        if (!container) {
            return;
        }

        const navigationType: string = container.dataset.cardNavigation;
        const isGridItemContent: boolean = target.classList.contains('o-grid__item-content');
        const isRowItem: boolean = target.classList.contains('o-row__item');
        const isLink: boolean = target.tagName === 'A';        
        // Only handle navigation if we're on a focusable card element
        if (!isLink && !target.hasAttribute('tabindex')) {
            return;
        }        
        // Ensure we're actually on a card element
        if (!isGridItemContent && !isRowItem) {
            return;
        }

        switch (event.key) {
            case Key.ArrowLeft:
                event.preventDefault();
                this.navigateToCard(container, target, 'left', navigationType);
                break;
            case Key.ArrowRight:
                event.preventDefault();
                this.navigateToCard(container, target, 'right', navigationType);
                break;
            case Key.ArrowUp:
                if (navigationType === 'grid') {
                    event.preventDefault();
                    this.navigateToCard(container, target, 'up', navigationType);
                }
                else if (navigationType === 'row') {
                    event.preventDefault();
                    this.navigateRowVertical(container, target, 'up');
                }
                break;
            case Key.ArrowDown:
                if (navigationType === 'grid') {
                    event.preventDefault();
                    this.navigateToCard(container, target, 'down', navigationType);
                }
                else if (navigationType === 'row') {
                    event.preventDefault();
                    this.navigateRowVertical(container, target, 'down');
                }
                break;
        }
    }

    private navigateRowVertical(currentRow: HTMLElement, currentElement: HTMLElement, direction: 'up' | 'down'): void {
        const pageRoot: HTMLElement = (currentRow.closest('[data-page-type]') as HTMLElement) || document.body;
        const rows: HTMLElement[] = Array.from(pageRoot.querySelectorAll("[data-card-navigation='row']")) as HTMLElement[];
        if (!rows.length) {
            return;
        }

        const currentRowIndex: number = rows.indexOf(currentRow);
        if (currentRowIndex === -1) {
            return;
        }
        const targetRowIndex: number = direction === 'up' ? currentRowIndex - 1 : currentRowIndex + 1;
        if (targetRowIndex < 0 || targetRowIndex >= rows.length) {
            return;
        }
        const targetRow: HTMLElement = rows[targetRowIndex];
        const currentCards: HTMLElement[] = Array.from(currentRow.querySelectorAll('.o-row__item')) as HTMLElement[];
        const currentCardIndex: number = currentCards.indexOf(currentElement);
        const targetCards: HTMLElement[] = Array.from(targetRow.querySelectorAll('.o-row__item')) as HTMLElement[];
        if (!targetCards.length) {
            return;
        }
        const targetIndex: number = currentCardIndex >= 0 ? Math.min(currentCardIndex, targetCards.length - 1) : 0;
        const targetCard: HTMLElement = targetCards[targetIndex];
        targetCard.focus();
        this.scrollIntoViewIfNeeded(targetCard);
    }

    private findNavigationContainer(element: HTMLElement): HTMLElement | null {
        let current = element;
        while (current && current !== document.body) {
            if (current.dataset.cardNavigation) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }

    private navigateToCard(container: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right' | 'up' | 'down', navigationType: string): void {
        if (navigationType === 'grid') {
            this.navigateGrid(container, currentElement, direction);
        } 
        else if (navigationType === 'row') {
            // Only allow left/right navigation for rows
            if (direction === 'left' || direction === 'right') {
                this.navigateRow(container, currentElement, direction);
            }
        }
    }

    private navigateGrid(gridContainer: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right' | 'up' | 'down'): void {
        const cards: HTMLElement[] = Array.from(gridContainer.querySelectorAll('.o-grid__item-content')) as HTMLElement[];
        const currentIndex: number = cards.indexOf(currentElement);
        
        if (currentIndex === -1) {
            return;
        }

        // Calculate grid dimensions by analyzing CSS grid layout
        const gridItemsContainer: HTMLElement = gridContainer.querySelector('.o-grid__items');
        const gridStyle: CSSStyleDeclaration = window.getComputedStyle(gridItemsContainer);
        const gridTemplateColumns: string = gridStyle.gridTemplateColumns;
        
        // Extract number of columns from grid-template-columns
        let columnsCount: number = 1;
        if (gridTemplateColumns && gridTemplateColumns !== 'none') {
            columnsCount = gridTemplateColumns.split(' ').length;
        }        
        // Fallback: calculate columns based on item widths if CSS Grid info is not available
        if (columnsCount === 1 && cards.length > 1) {
            const containerWidth = gridItemsContainer.clientWidth;
            const itemWidth = cards[0].offsetWidth;
            if (itemWidth > 0) {
                columnsCount = Math.floor(containerWidth / itemWidth);
            }
        }

        let targetIndex: number = currentIndex;
        switch (direction) {
            case 'left':
                targetIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
                break;
            case 'right':
                targetIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'up':
                targetIndex = currentIndex - columnsCount;
                if (targetIndex < 0) {
                    // Wrap to bottom row
                    const remainingSlots: number = cards.length % columnsCount;
                    const lastRowStart: number = cards.length - (remainingSlots || columnsCount);
                    const columnPosition: number = currentIndex % columnsCount;
                    targetIndex = lastRowStart + Math.min(columnPosition, (remainingSlots || columnsCount) - 1);
                }
                break;
            case 'down':
                targetIndex = currentIndex + columnsCount;
                if (targetIndex >= cards.length) {
                    // Wrap to top row
                    const columnPosition: number = currentIndex % columnsCount;
                    targetIndex = Math.min(columnPosition, cards.length - 1);
                }
                break;
        }

        if (targetIndex >= 0 && targetIndex < cards.length && targetIndex !== currentIndex) {
            const targetCard: HTMLElement = cards[targetIndex];
            targetCard.focus();
            this.scrollIntoViewIfNeeded(targetCard);
        }
    }

    private navigateRow(rowContainer: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right'): void {
        const cards: HTMLElement[] = Array.from(rowContainer.querySelectorAll('.o-row__item')) as HTMLElement[];
        const currentIndex: number = cards.indexOf(currentElement);        
        if (currentIndex === -1) {
            return;
        }

        let targetIndex: number;
        switch (direction) {
            case 'left':
                targetIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
                break;
            case 'right':
                targetIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
                break;
            default:
                return;
        }
        if (targetIndex >= 0 && targetIndex < cards.length && targetIndex !== currentIndex) {
            const targetCard: HTMLElement = cards[targetIndex];
            targetCard.focus();
            this.scrollIntoViewIfNeeded(targetCard);
        }
    }

    private scrollIntoViewIfNeeded(element: HTMLElement): void {
        const rect: DOMRectReadOnly = element.getBoundingClientRect();
        const isInViewport: boolean = rect.top >= 0 && 
            rect.left >= 0 && 
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && 
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest', 
                inline: 'nearest' 
            });
        }
    }
}